<script setup lang="ts">
import { computed, ref } from "vue";
import { isJust, isNothing, type Maybe } from "../lib/maybe";
import {
  coverAssetName,
  importProjectAsset,
  pickImageFile,
  removeProjectAsset,
} from "../lib/tauri/assets";
import { isTauri } from "../lib/runtime";
import ZwButton from "./ui/ZwButton.vue";

const coverImage = defineModel<string>("coverImage", { required: true });

const props = defineProps<{
  rootPath: Maybe<string>;
  /** Cached data URL, kept fresh by the parent regardless of which tab is
   * open (so it's ready the moment this tab mounts). */
  previewSrc: string;
}>();

const choosingCover = ref(false);

const canManageCover = computed(() => {
  const root = props.rootPath;
  return isJust(root) && root.value.length > 0 && isTauri();
});

async function chooseCover(): Promise<void> {
  const root = props.rootPath;
  if (
    isNothing(root) ||
    canManageCover.value === false ||
    choosingCover.value === true
  ) {
    return;
  }
  const source = await pickImageFile();
  if (source === null) return;

  choosingCover.value = true;
  const previous = coverImage.value.trim();
  try {
    const assetName = coverAssetName(source);
    const relative = await importProjectAsset(root.value, source, assetName);
    if (previous.length > 0 && previous !== relative) {
      await removeProjectAsset(root.value, previous);
    }
    coverImage.value = relative;
  } catch (error) {
    console.error("Failed to import cover image", error);
    window.alert(
      error instanceof Error ? error.message : "Could not add cover image.",
    );
  } finally {
    choosingCover.value = false;
  }
}

function removeCover(): void {
  coverImage.value = "";
}
</script>

<template>
  <div class="cover-field">
    <span class="zw-field-label">Cover</span>
    <img
      v-if="previewSrc.length > 0"
      :src="previewSrc"
      alt="Book cover"
      class="cover-tile cover-image"
    />
    <button
      v-else
      type="button"
      class="cover-tile cover-empty"
      :disabled="!canManageCover || choosingCover"
      @click="chooseCover"
    >
      <svg
        class="cover-empty-icon"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <circle cx="9" cy="9" r="2" />
        <path d="m21 15-3.1-3.1a2 2 0 0 0-2.8 0L6 21" />
      </svg>
      <span>{{ choosingCover ? "Adding…" : "Add cover" }}</span>
    </button>

    <div v-if="previewSrc.length > 0" class="cover-actions">
      <ZwButton
        size="sm"
        :disabled="!canManageCover || choosingCover"
        @click="chooseCover"
      >
        {{ choosingCover ? "Adding…" : "Replace" }}
      </ZwButton>
      <ZwButton
        size="sm"
        :disabled="!canManageCover || choosingCover"
        @click="removeCover"
      >
        Remove
      </ZwButton>
    </div>
  </div>
</template>

<style scoped>
.cover-field {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  font-family: var(--font-ui);
  color: var(--text-muted);
}

/* Kindle's recommended cover shape: 1600 × 2560. */
.cover-tile {
  display: block;
  width: 100%;
  aspect-ratio: 5 / 8;
  border-radius: 0.3rem;
}

.cover-image {
  object-fit: cover;
  box-shadow:
    0 1px 2px color-mix(in oklch, black 30%, transparent),
    0 6px 18px color-mix(in oklch, black 22%, transparent);
}

.cover-empty {
  appearance: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border: 1px dashed color-mix(in oklch, var(--line) 90%, var(--text-muted));
  background: color-mix(in oklch, var(--bg) 55%, var(--bg-raised));
  color: var(--text-muted);
  font: inherit;
  font-size: 0.85rem;
  cursor: pointer;
  transition:
    border-color 120ms ease,
    background-color 120ms ease,
    color 120ms ease;
}

.cover-empty:hover:not(:disabled),
.cover-empty:focus-visible {
  border-color: var(--color-zw-amber);
  background: color-mix(in oklch, var(--color-zw-amber) 8%, var(--bg));
  color: var(--text-strong);
}

.cover-empty:disabled {
  cursor: default;
  opacity: 0.6;
}

.cover-empty-icon {
  width: 1.6rem;
  height: 1.6rem;
}

.cover-actions {
  display: flex;
  gap: 0.35rem;
}
</style>
